// The picker model of the LIVERY section of the Fleet Designer card
// (monetization.md §3.2, R1252). It holds no DOM, so a test drives it.
//
// The account holds one livery or NONE (''). The card shows NONE and the
// twenty options in two groups, a press picks one, and SAVE sends the pick:
// a code through PUT, NONE through the DELETE, which needs no key.

import { LIVERIES, type LiveryCode } from '../../../gen/presentation';

/** One pick: a code of the list, or '' for NONE. */
export type LiveryPick = LiveryCode | '';

/** The draft of the section: what the server holds, and what the card
 *  shows picked. */
export interface LiveryDraft {
  stored: LiveryPick;
  picked: LiveryPick;
}

/** The two groups of the grid, in the row order of the list. */
export const LIVERY_GROUPS: readonly { family: 'pattern' | 'decal'; codes: readonly LiveryCode[] }[] = [
  { family: 'pattern', codes: LIVERIES.filter((r) => r.family === 'pattern').map((r) => r.code) },
  { family: 'decal', codes: LIVERIES.filter((r) => r.family === 'decal').map((r) => r.code) },
];

/** A stored code as a pick. A code this build does not know reads NONE,
 *  as the world draws it. */
export function asPick(code: string): LiveryPick {
  return LIVERIES.some((r) => r.code === code) ? code as LiveryCode : '';
}

/** The draft made from the stored code. */
export function liveryDraftOf(stored: string): LiveryDraft {
  const s = asPick(stored);
  return { stored: s, picked: s };
}

/** One press on a cell. It answers false for a code off the list. */
export function pickLivery(d: LiveryDraft, code: string): boolean {
  if (code !== '' && asPick(code) === '') return false;
  d.picked = code as LiveryPick;
  return true;
}

/** True while the pick differs from what the server holds. */
export function liveryChanged(d: LiveryDraft): boolean {
  return d.picked !== d.stored;
}

/** What SAVE sends: nothing, a PUT of the code, or the DELETE of NONE. */
export function liverySave(d: LiveryDraft): 'none' | 'put' | 'delete' {
  if (!liveryChanged(d)) return 'none';
  return d.picked === '' ? 'delete' : 'put';
}
